import React, { useState, useEffect, useMemo } from 'react';
import {
  Tabs,
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  message,
  Popconfirm,
  Tag,
  Select,
  Upload,
  Typography,
  Card,
  Descriptions,
  Checkbox,
  InputNumber,
  Divider,
  Alert,
  Spin
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UploadOutlined,
  PlayCircleOutlined,
  FileTextOutlined,
  CloudOutlined,
  FolderOutlined
} from '@ant-design/icons';
import {
  deploymentService,
  AppDeploymentGroup,
  DockerImage,
  DeploymentServer,
  DeploymentConfig,
  DeploymentRecord
} from '../services/deploymentService';
import './common.css';
import { useI18n } from '../hooks/useI18n';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { TabPane } = Tabs;

const DEFAULT_REGISTRY_URL = 'registry-1.docker.io';
const DEFAULT_REGISTRY_PROJECT = 'library';

const DeploymentManagement: React.FC = () => {
  const { t } = useI18n();
  // 版本组状态
  const [groups, setGroups] = useState<AppDeploymentGroup[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<AppDeploymentGroup | null>(null);
  const [groupModalVisible, setGroupModalVisible] = useState(false);
  const [groupForm] = Form.useForm();

  // 镜像状态
  const [images, setImages] = useState<DockerImage[]>([]);
  const [imageModalVisible, setImageModalVisible] = useState(false);
  const [imageForm] = Form.useForm();
  const [uploading, setUploading] = useState(false);
  const [uploadConfirmModalVisible, setUploadConfirmModalVisible] = useState(false);
  const [uploadConfirmForm] = Form.useForm();
  const [pendingUploadFile, setPendingUploadFile] = useState<File | null>(null);

  // 服务器状态
  const [servers, setServers] = useState<DeploymentServer[]>([]);
  const [serverModalVisible, setServerModalVisible] = useState(false);
  const [serverForm] = Form.useForm();
  const [editingServer, setEditingServer] = useState<DeploymentServer | null>(null);
  const [viewServerModalVisible, setViewServerModalVisible] = useState(false);
  const [viewServer, setViewServer] = useState<DeploymentServer | null>(null);

  // 配置文件状态
  const [configs, setConfigs] = useState<DeploymentConfig[]>([]);
  const [configModalVisible, setConfigModalVisible] = useState(false);
  const [configForm] = Form.useForm();
  const [configPathTouched, setConfigPathTouched] = useState(false);
  const [viewConfigModalVisible, setViewConfigModalVisible] = useState(false);
  const [viewConfig, setViewConfig] = useState<DeploymentConfig | null>(null);
  const [viewConfigLoading, setViewConfigLoading] = useState(false);
  const [downloadingConfigId, setDownloadingConfigId] = useState<number | null>(null);

  // 部署状态
  const [deploying, setDeploying] = useState(false);
  const [deployLogs, setDeployLogs] = useState<string[]>([]);
  const [deployModalVisible, setDeployModalVisible] = useState(false);
  const [deployForm] = Form.useForm();
  const selectedDeployAppName = Form.useWatch('app_name', deployForm);
  const selectedDeployGroupId = Form.useWatch('group_id', deployForm);
  const [records, setRecords] = useState<DeploymentRecord[]>([]);

  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('groups');
  const [groupConfigsCache, setGroupConfigsCache] = useState<Record<number, DeploymentConfig[]>>({});
  const [fullImageNameTouched, setFullImageNameTouched] = useState(false);

  const appNameOptions = useMemo(() => Array.from(new Set(groups.map(group => group.app_name))), [groups]);
  const filteredGroups = useMemo(
    () => groups.filter(group => !selectedDeployAppName || group.app_name === selectedDeployAppName),
    [groups, selectedDeployAppName]
  );

  useEffect(() => {
    if (!selectedDeployGroupId || groupConfigsCache[selectedDeployGroupId]) {
      return;
    }

    const fetchConfigs = async () => {
      try {
        const data = await deploymentService.getConfigsByGroup(selectedDeployGroupId);
        setGroupConfigsCache(prev => ({ ...prev, [selectedDeployGroupId]: data }));
      } catch (error: any) {
        message.error('加载配置文件失败');
      }
    };

    fetchConfigs();
  }, [selectedDeployGroupId, groupConfigsCache]);

  useEffect(() => {
    loadGroups();
    loadServers();
  }, []);

  useEffect(() => {
    if (selectedGroup) {
      loadImages(selectedGroup.id!);
      loadConfigs(selectedGroup.id!);
    }
  }, [selectedGroup]);

  // ========== 版本组管理 ==========
  const loadGroups = async () => {
    try {
      setLoading(true);
      const data = await deploymentService.getGroups();
      setGroups(data);
    } catch (error: any) {
      message.error(t('deploy.messages.loadGroupsFail'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGroup = async (values: any) => {
    try {
      await deploymentService.createGroup(values);
      message.success(t('deploy.messages.createGroupSuccess'));
      setGroupModalVisible(false);
      groupForm.resetFields();
      loadGroups();
    } catch (error: any) {
      message.error(error.response?.data?.error || t('deploy.messages.actionFail'));
    }
  };

  const handleDeleteGroup = async (id: number) => {
    try {
      await deploymentService.deleteGroup(id);
      message.success(t('deploy.messages.deleteSuccess'));
      loadGroups();
    } catch (error: any) {
      message.error(t('deploy.messages.deleteFail'));
    }
  };

  // ========== 镜像管理 ==========
  const loadImages = async (groupId: number) => {
    try {
      const data = await deploymentService.getImagesByGroup(groupId);
      setImages(data);
    } catch (error: any) {
      message.error(t('deploy.messages.loadImagesFail'));
    }
  };

  /**
   * 从文件名解析组件名
   * 支持格式：
   * 1. 组件名-版本-日期.tar (如 bootstrap-v1.0-202508080630.tar)
   * 2. 组件名_版本.tar.gz (如 minigoweb_v1.0.tar.gz)
   * 3. 组件名-版本.tar (如 bootstrap-v1.0.tar)
   */
  const parseComponentNameFromFileName = (fileName: string): string | null => {
    // 移除 .tar 或 .tar.gz 扩展名
    const nameWithoutExt = fileName.replace(/\.tar\.gz$/, '').replace(/\.tar$/, '');
    
    // 首先尝试匹配带日期的格式：组件名-版本-日期 或 组件名_版本_日期
    let match = nameWithoutExt.match(/^(.+?)[\-_](.+?)[\-_](.+)$/);
    if (match) {
      return match[1];
    }
    
    // 尝试匹配不带日期的格式：组件名-版本 或 组件名_版本
    match = nameWithoutExt.match(/^(.+?)[\-_](.+)$/);
    if (match) {
      return match[1];
    }
    
    return null;
  };

  const handleUploadImage = async (file: File, groupId: number, componentName: string, appName: string) => {
    try {
      setUploading(true);
      await deploymentService.uploadImage(groupId, componentName, appName, file);
      message.success(t('deploy.messages.uploadSuccess'));
      if (selectedGroup) {
        loadImages(selectedGroup.id!);
      }
      setUploadConfirmModalVisible(false);
      setPendingUploadFile(null);
      uploadConfirmForm.resetFields();
    } catch (error: any) {
      message.error(error.response?.data?.error || t('deploy.messages.uploadFail'));
    } finally {
      setUploading(false);
    }
  };

  const handleFileSelect = (file: File) => {
    const componentName = parseComponentNameFromFileName(file.name);
    if (!componentName) {
      message.error(t('deploy.messages.parseComponentFail'));
      return false;
    }
    
    // 设置待上传文件和组件名，显示确认对话框
    setPendingUploadFile(file);
    uploadConfirmForm.setFieldsValue({
      component_name: componentName,
      file_name: file.name
    });
    setUploadConfirmModalVisible(true);
    return false; // 阻止自动上传
  };

  const handleConfirmUpload = async () => {
    if (!pendingUploadFile || !selectedGroup) {
      return;
    }
    
    const values = uploadConfirmForm.getFieldsValue();
    await handleUploadImage(
      pendingUploadFile,
      selectedGroup.id!,
      values.component_name,
      selectedGroup.app_name
    );
  };

  const handleCreateCloudImage = async (values: any) => {
    try {
      await deploymentService.createCloudImage(values);
      message.success(t('deploy.messages.createCloudImageSuccess'));
      setImageModalVisible(false);
      imageForm.resetFields();
      setFullImageNameTouched(false);
      if (selectedGroup) {
        loadImages(selectedGroup.id!);
      }
    } catch (error: any) {
      message.error(error.response?.data?.error || t('deploy.messages.actionFail'));
    }
  };

  const handleImageFormValuesChange = (changedValues: any, allValues: any) => {
    if ('full_image_name' in changedValues) {
      setFullImageNameTouched(true);
      return;
    }

    const registryUrl = allValues.registry_url || DEFAULT_REGISTRY_URL;
    const registryProject = allValues.registry_project || DEFAULT_REGISTRY_PROJECT;

    if (fullImageNameTouched) {
      return;
    }

    const basePath =
      registryUrl && registryProject ? `${registryUrl}/${registryProject}` : registryUrl || '';

    if (basePath && allValues.component_name && allValues.image_name) {
      const autoFullName = `${basePath}/${allValues.component_name}:${allValues.image_name}`;
      if (autoFullName !== allValues.full_image_name) {
        imageForm.setFieldsValue({ full_image_name: autoFullName });
      }
    } else if (basePath && !allValues.full_image_name?.startsWith(basePath)) {
      imageForm.setFieldsValue({ full_image_name: `${basePath}/` });
    }
  };

  const handleDeleteImage = async (id: number) => {
    try {
      await deploymentService.deleteImage(id);
      message.success(t('deploy.messages.deleteSuccess'));
      if (selectedGroup) {
        loadImages(selectedGroup.id!);
      }
    } catch (error: any) {
      message.error(t('deploy.messages.deleteFail'));
    }
  };

  // ========== 租户服务器管理 ==========
  const loadServers = async () => {
    try {
      const data = await deploymentService.getServers();
      setServers(data);
    } catch (error: any) {
      message.error(t('deploy.messages.loadServersFail'));
    }
  };

  const handleSubmitServer = async (values: any) => {
    try {
      if (editingServer?.id) {
        await deploymentService.updateServer(editingServer.id, values);
        message.success(t('deploy.messages.updateServerSuccess'));
      } else {
        await deploymentService.createServer(values);
        message.success(t('deploy.messages.createServerSuccess'));
      }
      setServerModalVisible(false);
      setEditingServer(null);
      serverForm.resetFields();
      loadServers();
    } catch (error: any) {
      message.error(error.response?.data?.error || t('deploy.messages.actionFail'));
    }
  };

  const handleDeleteServer = async (id: number) => {
    try {
      await deploymentService.deleteServer(id);
      message.success(t('deploy.messages.deleteSuccess'));
      loadServers();
    } catch (error: any) {
      message.error(t('deploy.messages.deleteFail'));
    }
  };

  // ========== 配置文件管理 ==========
  const loadConfigs = async (groupId: number) => {
    try {
      const data = await deploymentService.getConfigsByGroup(groupId);
      setConfigs(data);
      setGroupConfigsCache(prev => ({
        ...prev,
        [groupId]: data
      }));
    } catch (error: any) {
      message.error(t('deploy.messages.loadConfigsFail'));
    }
  };

  const buildConfigPath = (tenantCode?: string, appName?: string, configName?: string) => {
    if (!appName) {
      return '';
    }
    const tenantSegment = (tenantCode?.trim() || 'default').replace(/^\/+|\/+$/g, '');
    const appSegment = appName.trim();
    let path = `~/B1/${tenantSegment}/${appSegment}`;
    const fileSegment = configName?.trim();
    path += fileSegment ? `/${fileSegment}` : '/';
    return path;
  };

  const handleConfigFormValuesChange = (changedValues: any, allValues: any) => {
    if (!selectedGroup || configPathTouched) {
      return;
    }
    if ('config_path' in changedValues) {
      return;
    }
    if ('config_name' in changedValues || 'tenant_code' in changedValues) {
      const newPath = buildConfigPath(allValues.tenant_code, selectedGroup.app_name, allValues.config_name);
      if (newPath && newPath !== allValues.config_path) {
        configForm.setFieldsValue({ config_path: newPath });
      }
    }
  };

  const handleCreateConfig = async (values: any) => {
    try {
      if (!selectedGroup) {
        message.error(t('deploy.messages.selectGroupFirst'));
        return;
      }
      await deploymentService.createConfig({
        ...values,
        app_name: selectedGroup.app_name,
        group_id: selectedGroup.id
      });
      message.success(t('deploy.messages.createConfigSuccess'));
      setConfigModalVisible(false);
      configForm.resetFields();
      setConfigPathTouched(false);
      loadConfigs(selectedGroup.id!);
    } catch (error: any) {
      message.error(error.response?.data?.error || t('deploy.messages.actionFail'));
    }
  };

  const handleDeleteConfig = async (id: number) => {
    try {
      await deploymentService.deleteConfig(id);
      message.success(t('deploy.messages.deleteSuccess'));
      if (selectedGroup) {
        loadConfigs(selectedGroup.id!);
      }
    } catch (error: any) {
      message.error(t('deploy.messages.deleteFail'));
    }
  };

  const handleViewConfig = async (id: number) => {
    try {
      setViewConfigLoading(true);
      const data = await deploymentService.getConfig(id);
      setViewConfig(data);
      setViewConfigModalVisible(true);
    } catch (error: any) {
      message.error(t('deploy.messages.loadConfigsFail'));
    } finally {
      setViewConfigLoading(false);
    }
  };

  const extractFileNameFromPath = (configPath?: string, configName?: string) => {
    if (configPath) {
      const segments = configPath.split('/');
      const lastSegment = segments[segments.length - 1];
      if (lastSegment) {
        return lastSegment;
      }
    }
    return configName || 'config.conf';
  };

  const handleDownloadConfig = async (id: number) => {
    try {
      setDownloadingConfigId(id);
      const data = await deploymentService.getConfig(id);
      const fileName = extractFileNameFromPath(data.config_path, data.config_name);
      const blob = new Blob([data.config_content || ''], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error: any) {
      message.error(t('deploy.messages.downloadConfigFail'));
    } finally {
      setDownloadingConfigId(null);
    }
  };

  // ========== 部署管理 ==========
  const handleDeploy = async (values: any) => {
    // 防抖：如果正在部署中，直接返回
    if (deploying) {
      message.warning(t('deploy.messages.deploying'));
      return;
    }

    const selectedServer = servers.find(server => server.id === values.server_id);
    if (!selectedServer || !selectedServer.id) {
      message.error(t('deploy.messages.selectValidServer'));
      return;
    }

    let configsForGroup = groupConfigsCache[values.group_id];
    if (!configsForGroup) {
      try {
        const fetchedConfigs = await deploymentService.getConfigsByGroup(values.group_id);
        configsForGroup = fetchedConfigs;
        setGroupConfigsCache(prev => ({ ...prev, [values.group_id]: fetchedConfigs }));
      } catch (error: any) {
        message.error(t('deploy.messages.loadConfigsFail'));
        return;
      }
    }

    const configIds = (configsForGroup || [])
      .map(config => config.id)
      .filter((id): id is number => typeof id === 'number');

    try {
      setDeploying(true);
      setDeployLogs([]);
      
      await deploymentService.deploy(
        selectedServer.tenant_code,
        values.app_name,
        values.group_id,
        selectedServer.id,
        configIds,
        (log) => {
          setDeployLogs(prev => [...prev, log]);
        }
      );

      message.success(t('deploy.messages.deploySuccess'));
      setDeployModalVisible(false);
      deployForm.resetFields();
    } catch (error: any) {
      message.error(error.message || t('deploy.messages.deployFail'));
    } finally {
      setDeploying(false);
    }
  };

  const loadRecords = async () => {
    try {
      const data = await deploymentService.getDeploymentRecords();
      setRecords(data);
    } catch (error: any) {
      message.error(t('deploy.messages.loadRecordsFail'));
    }
  };

  // ========== 表格列定义 ==========
  const groupColumns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 80 },
    { title: t('deploy.table.appName'), dataIndex: 'app_name', key: 'app_name' },
    { title: t('deploy.table.groupName'), dataIndex: 'group_name', key: 'group_name' },
    { title: t('deploy.table.description'), dataIndex: 'description', key: 'description' },
    { title: t('deploy.table.status'), dataIndex: 'status', key: 'status', render: (status: string) => <Tag>{status}</Tag> },
    {
      title: t('deploy.table.actions'),
      key: 'action',
      render: (_: any, record: AppDeploymentGroup) => (
        <Space>
          <Button
            type="link"
            onClick={() => {
              setSelectedGroup(record);
              setActiveTab('images');
            }}
          >
            {t('deploy.actions.viewImages')}
          </Button>
          <Popconfirm title={t('deploy.messages.confirmDelete')} onConfirm={() => handleDeleteGroup(record.id!)}>
            <Button type="link" danger>{t('common.delete')}</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const imageColumns = [
    { title: t('deploy.table.componentName'), dataIndex: 'component_name', key: 'component_name' },
    { title: t('deploy.table.imageName'), dataIndex: 'image_name', key: 'image_name' },
    {
      title: t('deploy.table.imageType'),
      dataIndex: 'image_type',
      key: 'image_type',
      render: (type: string) => (
        <Tag color={type === 'cloud' ? 'blue' : 'green'}>
          {type === 'cloud' ? <CloudOutlined /> : <FolderOutlined />} {type}
        </Tag>
      ),
    },
    { title: t('deploy.table.version'), dataIndex: 'version', key: 'version' },
    {
      title: t('deploy.table.actions'),
      key: 'action',
      render: (_: any, record: DockerImage) => (
        <Popconfirm title={t('deploy.messages.confirmDelete')} onConfirm={() => handleDeleteImage(record.id!)}>
          <Button type="link" danger>{t('common.delete')}</Button>
        </Popconfirm>
      ),
    },
  ];

  const handleViewServer = (record: DeploymentServer) => {
    setViewServer(record);
    setViewServerModalVisible(true);
  };

  const handleEditServer = (record: DeploymentServer) => {
    setEditingServer(record);
    setServerModalVisible(true);
    serverForm.setFieldsValue({
      tenant_code: record.tenant_code,
      server_address: record.server_address,
      ssh_user: record.ssh_user,
      ssh_port: record.ssh_port || 22,
      ssh_key_path: record.ssh_key_path || '~/.ssh/id_rsa'
    });
  };

  const openCreateServerModal = () => {
    setEditingServer(null);
    serverForm.resetFields();
    serverForm.setFieldsValue({
      ssh_port: 22,
      ssh_key_path: '~/.ssh/id_rsa'
    });
    setServerModalVisible(true);
  };

  const serverColumns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 80 },
    { title: t('deploy.fields.tenantCode'), dataIndex: 'tenant_code', key: 'tenant_code' },
    { title: t('deploy.fields.serverAddress'), dataIndex: 'server_address', key: 'server_address' },
    { title: t('deploy.fields.sshUser'), dataIndex: 'ssh_user', key: 'ssh_user' },
    { title: t('deploy.fields.sshPort'), dataIndex: 'ssh_port', key: 'ssh_port' },
    { title: t('deploy.table.status'), dataIndex: 'status', key: 'status', render: (status: string) => <Tag>{status}</Tag> },
    {
      title: t('deploy.table.actions'),
      key: 'action',
      render: (_: any, record: DeploymentServer) => (
        <Space>
          <Button type="link" onClick={() => handleEditServer(record)}>{t('common.edit')}</Button>
          <Button type="link" onClick={() => handleViewServer(record)}>{t('deploy.actions.view')}</Button>
          <Popconfirm title={t('deploy.messages.confirmDelete')} onConfirm={() => handleDeleteServer(record.id!)}>
            <Button type="link" danger>{t('common.delete')}</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const configColumns = [
    { title: t('deploy.fields.tenant'), dataIndex: 'tenant_code', key: 'tenant_code', width: 140 },
    { title: t('deploy.fields.configName'), dataIndex: 'config_name', key: 'config_name' },
    { title: t('deploy.fields.configPath'), dataIndex: 'config_path', key: 'config_path', ellipsis: true },
    {
      title: t('deploy.table.actions'),
      key: 'action',
      render: (_: any, record: DeploymentConfig) => (
        <Space>
          <Button
            type="link"
            onClick={() => handleDownloadConfig(record.id!)}
            loading={downloadingConfigId === record.id}
          >
            {t('common.download')}
          </Button>
          <Button type="link" onClick={() => handleViewConfig(record.id!)}>{t('deploy.actions.view')}</Button>
          <Popconfirm title={t('deploy.messages.confirmDelete')} onConfirm={() => handleDeleteConfig(record.id!)}>
            <Button type="link" danger>{t('common.delete')}</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const recordColumns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 80 },
    { title: t('deploy.fields.tenant'), dataIndex: 'tenant_code', key: 'tenant_code' },
    { title: t('deploy.fields.app'), dataIndex: 'app_name', key: 'app_name' },
    { title: t('deploy.table.status'), dataIndex: 'status', key: 'status', render: (status: string) => <Tag>{status}</Tag> },
    { title: t('deploy.fields.startedAt'), dataIndex: 'started_at', key: 'started_at' },
    { title: t('deploy.fields.completedAt'), dataIndex: 'completed_at', key: 'completed_at' },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>{t('deploy.title')}</Title>

      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        {/* 版本组管理 */}
        <TabPane tab={t('deploy.tabs.groups')} key="groups">
          <Space style={{ marginBottom: 16 }}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                groupForm.resetFields();
                setGroupModalVisible(true);
              }}
            >
              {t('deploy.actions.createGroup')}
            </Button>
          </Space>
          <Table
            columns={groupColumns}
            dataSource={groups}
            rowKey="id"
            loading={loading}
          />
        </TabPane>

        {/* 镜像管理 */}
        <TabPane tab={t('deploy.tabs.images')} key="images">
          {selectedGroup ? (
            <>
              <Card style={{ marginBottom: 16 }}>
                <Descriptions title={t('deploy.currentGroup')} bordered size="small">
                  <Descriptions.Item label={t('deploy.fields.appName')}>{selectedGroup.app_name}</Descriptions.Item>
                  <Descriptions.Item label={t('deploy.fields.groupName')}>{selectedGroup.group_name}</Descriptions.Item>
                  <Descriptions.Item label={t('deploy.fields.description')}>{selectedGroup.description || '-'}</Descriptions.Item>
                </Descriptions>
              </Card>
              <Space style={{ marginBottom: 16 }}>
                <Upload
                  beforeUpload={handleFileSelect}
                  showUploadList={false}
                  accept=".tar,.tar.gz"
                >
                  <Button icon={<UploadOutlined />} loading={uploading}>{t('deploy.actions.uploadTar')}</Button>
                </Upload>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    imageForm.setFieldsValue({
                      group_id: selectedGroup.id,
                      image_type: 'cloud',
                      registry_url: DEFAULT_REGISTRY_URL,
                      registry_project: DEFAULT_REGISTRY_PROJECT,
                      full_image_name: `${DEFAULT_REGISTRY_URL}/${DEFAULT_REGISTRY_PROJECT}/`
                    });
                    setFullImageNameTouched(false);
                    setImageModalVisible(true);
                  }}
                >
                  {t('deploy.actions.addCloudImage')}
                </Button>
              </Space>
              <Table
                columns={imageColumns}
                dataSource={images}
                rowKey="id"
              />
            </>
          ) : (
            <Alert message={t('deploy.messages.selectGroupFirst')} type="info" />
          )}
        </TabPane>

        {/* 配置文件管理 */}
        <TabPane tab={t('deploy.tabs.configs')} key="configs">
          {selectedGroup ? (
            <>
              <Card style={{ marginBottom: 16 }}>
                <Descriptions title={t('deploy.currentGroup')} bordered size="small">
                  <Descriptions.Item label={t('deploy.fields.appName')}>{selectedGroup.app_name}</Descriptions.Item>
                  <Descriptions.Item label={t('deploy.fields.groupName')}>{selectedGroup.group_name}</Descriptions.Item>
                  <Descriptions.Item label={t('deploy.fields.description')}>{selectedGroup.description || '-'}</Descriptions.Item>
                </Descriptions>
              </Card>
              <Space style={{ marginBottom: 16 }}>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    const defaultTenant = 'default';
                    setConfigPathTouched(false);
                    configForm.setFieldsValue({
                      tenant_code: defaultTenant,
                      group_id: selectedGroup.id,
                      config_name: '',
                      config_path: buildConfigPath(defaultTenant, selectedGroup.app_name, '')
                    });
                    setConfigModalVisible(true);
                  }}
                >
                  {t('deploy.actions.createConfig')}
                </Button>
              </Space>
              <Table
                columns={configColumns}
                dataSource={configs}
                rowKey="id"
              />
            </>
          ) : (
            <Alert message={t('deploy.messages.selectGroupFirst')} type="info" />
          )}
        </TabPane>

        {/* 租户服务器管理 */}
        <TabPane tab={t('deploy.tabs.servers')} key="servers">
          <Space style={{ marginBottom: 16 }}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={openCreateServerModal}
            >
              {t('deploy.actions.addServer')}
            </Button>
          </Space>
          <Table
            columns={serverColumns}
            dataSource={servers}
            rowKey="id"
          />
        </TabPane>

        {/* 部署操作 */}
        <TabPane tab={t('deploy.tabs.deploy')} key="deploy">
          <Form
            form={deployForm}
            layout="vertical"
            onFinish={handleDeploy}
          >
            <Form.Item name="server_id" label={t('deploy.fields.server')} rules={[{ required: true }]}>
              <Select
                placeholder={t('deploy.placeholders.selectServer')}
                allowClear
                showSearch
                optionFilterProp="children"
              >
                {servers.map(server => (
                  <Select.Option key={server.id} value={server.id}>
                    {server.tenant_code}（{server.server_address}）
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="app_name" label={t('deploy.fields.appName')} rules={[{ required: true }]}>
              <Select placeholder={t('deploy.placeholders.selectApp')} allowClear showSearch optionFilterProp="children">
                {appNameOptions.map(appName => (
                  <Select.Option key={appName} value={appName}>
                    {appName}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="group_id" label={t('deploy.fields.group')} rules={[{ required: true }]}>
              <Select
                placeholder={selectedDeployAppName ? t('deploy.placeholders.selectGroup') : t('deploy.placeholders.selectAppFirst')}
                allowClear
                showSearch
                optionFilterProp="children"
                disabled={!selectedDeployAppName}
              >
                {filteredGroups.map(group => (
                  <Select.Option key={group.id} value={group.id}>
                    {group.group_name}（ID: {group.id}）
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                icon={<PlayCircleOutlined />}
                loading={deploying}
                disabled={deploying}
              >
                {t('deploy.actions.startDeploy')}
              </Button>
            </Form.Item>
          </Form>

          {deployLogs.length > 0 && (
            <Card title={t('deploy.logs.title')} style={{ marginTop: 16 }}>
              <div style={{ maxHeight: '400px', overflow: 'auto', fontFamily: 'monospace' }}>
                {deployLogs.map((log, index) => (
                  <div key={index}>{log}</div>
                ))}
              </div>
            </Card>
          )}
        </TabPane>

        {/* 部署记录 */}
        <TabPane tab={t('deploy.tabs.records')} key="records">
          <Button style={{ marginBottom: 16 }} onClick={loadRecords}>{t('deploy.actions.refresh')}</Button>
          <Table
            columns={recordColumns}
            dataSource={records}
            rowKey="id"
          />
        </TabPane>
      </Tabs>

      {/* 创建版本组Modal */}
      <Modal
        title={t('deploy.modal.createGroup')}
        open={groupModalVisible}
        onCancel={() => setGroupModalVisible(false)}
        onOk={() => groupForm.submit()}
      >
        <Form form={groupForm} onFinish={handleCreateGroup} layout="vertical">
          <Form.Item name="app_name" label={t('deploy.fields.appName')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="group_name" label={t('deploy.fields.groupName')} rules={[{ required: true }]}>
            <Input placeholder="v1.0-202508080630" />
          </Form.Item>
          <Form.Item name="description" label={t('deploy.fields.description')}>
            <TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      {/* 创建云镜像Modal */}
      <Modal
        title={t('deploy.modal.addCloudImage')}
        open={imageModalVisible}
        onCancel={() => {
          setImageModalVisible(false);
          imageForm.resetFields();
          setFullImageNameTouched(false);
        }}
        onOk={() => imageForm.submit()}
      >
        <Form
          form={imageForm}
          onFinish={handleCreateCloudImage}
          layout="vertical"
          onValuesChange={handleImageFormValuesChange}
        >
          <Form.Item name="group_id" label={t('deploy.fields.groupId')} rules={[{ required: true }]}>
            <InputNumber style={{ width: '100%' }} disabled />
          </Form.Item>
          <Form.Item name="component_name" label={t('deploy.fields.componentName')} rules={[{ required: true }]}>
            <Input placeholder={t('deploy.placeholders.componentName')} />
          </Form.Item>
          <Form.Item name="image_name" label={t('deploy.fields.imageNameTag')} rules={[{ required: true }]}>
            <Input placeholder="v1.0-202508080630" />
          </Form.Item>
          <Form.Item name="image_type" label={t('deploy.fields.imageType')}>
            <Select>
              <Select.Option value="cloud">{t('deploy.options.cloudImage')}</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="registry_url" label={t('deploy.fields.registryUrl')}>
            <Input placeholder="registry-1.docker.io" />
          </Form.Item>
          <Form.Item name="registry_project" label={t('deploy.fields.registryProject')}>
            <Input placeholder="library" />
          </Form.Item>
          <Form.Item name="full_image_name" label={t('deploy.fields.fullImageName')}>
            <Input placeholder="registry-1.docker.io/library/component:v1.0" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 创建服务器Modal */}
      <Modal
        title={editingServer ? t('deploy.modal.editServer') : t('deploy.modal.addServer')}
        open={serverModalVisible}
        onCancel={() => {
          setServerModalVisible(false);
          setEditingServer(null);
          serverForm.resetFields();
        }}
        onOk={() => serverForm.submit()}
      >
        <Form form={serverForm} onFinish={handleSubmitServer} layout="vertical">
          <Form.Item name="tenant_code" label={t('deploy.fields.tenant')} rules={[{ required: true }]}>
            <Input placeholder={t('deploy.placeholders.tenantCode')} />
          </Form.Item>
          <Form.Item name="server_address" label={t('deploy.fields.serverAddress')} rules={[{ required: true }]}>
            <Input placeholder={t('deploy.placeholders.serverAddress')} />
          </Form.Item>
          <Form.Item name="ssh_user" label={t('deploy.fields.sshUser')} rules={[{ required: true }]}>
            <Input placeholder={t('deploy.placeholders.sshUser')} />
          </Form.Item>
          <Form.Item name="ssh_port" label={t('deploy.fields.sshPort')}>
            <InputNumber defaultValue={22} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="ssh_key_path" label={t('deploy.fields.sshKeyPath')}>
            <Input placeholder="~/.ssh/id_rsa" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 查看租户服务器Modal */}
      <Modal
        title={t('deploy.modal.viewServer')}
        open={viewServerModalVisible}
        footer={null}
        onCancel={() => {
          setViewServerModalVisible(false);
          setViewServer(null);
        }}
      >
        {viewServer ? (
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="ID">{viewServer.id}</Descriptions.Item>
            <Descriptions.Item label={t('deploy.fields.tenantCode')}>{viewServer.tenant_code}</Descriptions.Item>
            <Descriptions.Item label={t('deploy.fields.serverAddress')}>{viewServer.server_address}</Descriptions.Item>
            <Descriptions.Item label={t('deploy.fields.sshUser')}>{viewServer.ssh_user}</Descriptions.Item>
            <Descriptions.Item label={t('deploy.fields.sshPort')}>{viewServer.ssh_port || 22}</Descriptions.Item>
            <Descriptions.Item label={t('deploy.fields.sshKeyPath')}>
              {viewServer.ssh_key_path || '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('deploy.table.status')}>
              <Tag>{viewServer.status || 'unknown'}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label={t('deploy.fields.createdAt')}>{viewServer.created_at || '-'}</Descriptions.Item>
            <Descriptions.Item label={t('deploy.fields.updatedAt')}>{viewServer.updated_at || '-'}</Descriptions.Item>
          </Descriptions>
        ) : (
          <Alert message={t('deploy.messages.serverNotFound')} type="warning" />
        )}
      </Modal>

      {/* 创建配置文件Modal */}
      <Modal
        title={t('deploy.modal.createConfig')}
        open={configModalVisible}
        onCancel={() => {
          setConfigModalVisible(false);
          setConfigPathTouched(false);
          configForm.resetFields();
        }}
        onOk={() => configForm.submit()}
      >
        <Form
          form={configForm}
          onFinish={handleCreateConfig}
          layout="vertical"
          onValuesChange={handleConfigFormValuesChange}
        >
          <Form.Item name="tenant_code" label={t('deploy.fields.tenantCode')} rules={[{ required: true }]}>
            <Input placeholder={t('deploy.placeholders.tenantCode')} />
          </Form.Item>
          <Form.Item name="config_name" label={t('deploy.fields.configName')} rules={[{ required: true }]}>
            <Input placeholder={t('deploy.placeholders.configName')} />
          </Form.Item>
          <Form.Item name="config_path" label={t('deploy.fields.configPath')} rules={[{ required: true }]}>
            <Input
              placeholder={t('deploy.placeholders.configPath')}
              onChange={() => setConfigPathTouched(true)}
            />
          </Form.Item>
          <Form.Item name="config_content" label={t('deploy.fields.configContent')}>
            <TextArea rows={10} />
          </Form.Item>
        </Form>
      </Modal>

      {/* 查看配置文件Modal */}
      <Modal
        title={t('deploy.modal.viewConfig')}
        open={viewConfigModalVisible}
        footer={null}
        onCancel={() => {
          setViewConfigModalVisible(false);
          setViewConfig(null);
        }}
      >
        {viewConfigLoading ? (
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <Spin />
          </div>
        ) : viewConfig ? (
          <>
            <Descriptions size="small" column={1} bordered>
              <Descriptions.Item label={t('deploy.fields.tenant')}>{viewConfig.tenant_code}</Descriptions.Item>
              <Descriptions.Item label={t('deploy.fields.app')}>{viewConfig.app_name}</Descriptions.Item>
              <Descriptions.Item label={t('deploy.fields.groupId')}>{viewConfig.group_id || '-'}</Descriptions.Item>
              <Descriptions.Item label={t('deploy.fields.configName')}>{viewConfig.config_name}</Descriptions.Item>
              <Descriptions.Item label={t('deploy.fields.configPath')}>{viewConfig.config_path}</Descriptions.Item>
            </Descriptions>
            <Divider />
            <TextArea
              rows={12}
              value={viewConfig.config_content || ''}
              readOnly
              style={{ fontFamily: 'monospace' }}
            />
          </>
        ) : (
          <Alert message={t('deploy.messages.configNotFound')} type="warning" />
        )}
      </Modal>

      {/* 上传确认Modal */}
      <Modal
        title={t('deploy.modal.uploadConfirm')}
        open={uploadConfirmModalVisible}
        onCancel={() => {
          setUploadConfirmModalVisible(false);
          setPendingUploadFile(null);
          uploadConfirmForm.resetFields();
        }}
        onOk={handleConfirmUpload}
        confirmLoading={uploading}
      >
        <Form form={uploadConfirmForm} layout="vertical">
          <Form.Item label={t('deploy.fields.fileName')}>
            <Input disabled value={uploadConfirmForm.getFieldValue('file_name')} />
          </Form.Item>
          <Form.Item
            name="component_name"
            label={t('deploy.fields.componentName')}
            rules={[{ required: true, message: t('deploy.messages.requiredComponentName') }]}
            tooltip={t('deploy.messages.componentNameTip')}
          >
            <Input placeholder={t('deploy.placeholders.componentName')} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default DeploymentManagement;

